import { gql, useMutation, useQuery } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import {
  Avatar,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import { TextField } from '@apollo/space-kit/TextField';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { referenceRouteConfig } from 'src/app/graph/routes';
import {
  MultiOrgSelect,
  MultiOrgSelectFragment,
} from 'src/app/graph/sandboxShared/MultiOrgSelect';
import { baseGraphSetupValidationSchema } from 'src/app/graph/shared/graphSetupHelpers';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphIDAvailableQuery } from 'src/hooks/useIsGraphIdAvailable';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { createUniqueName } from 'src/lib/createUniqueName';
import { generateUniqueString } from 'src/lib/generateUniqueString';
import { generateGraphId } from 'src/lib/graphOnboarding/helpers/utils';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { openStudioLinkFromEmbedInNewTab } from 'src/lib/routers/embedded';
import {
  setOverlayClosedLocation,
  setOverlayVisibleLocation,
} from 'src/lib/routing';

import { ErrorMessage } from '../errorMessage/ErrorMessage';
import { useCreateGraph } from '../graphSetupModal/useCreateGraph';
import { TextFieldWithStatus } from '../textFieldWithStatus/TextFieldWithStatus';

import { useUploadSandboxSchema } from './useUploadSandboxSchema';

const graphSetupValidationSchema = baseGraphSetupValidationSchema.concat(
  Yup.object().shape({
    copySandboxCollectionsToVariant: Yup.boolean().required(),
  }),
);

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof graphSetupValidationSchema>;

export const PublishToNewGraphModal = ({
  isOpen,
  onClose,
  currentAccountId,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentAccountId: string;
}) => {
  const { data, loading } = useQuery(
    gql<
      GraphQLTypes.PublishToNewGraphModalQuery,
      GraphQLTypes.PublishToNewGraphModalQueryVariables
    >`
      query PublishToNewGraphModalQuery {
        me {
          id
          ... on User {
            memberships {
              account {
                name
                id
                services {
                  id
                  title
                }
                ...MultiOrgSelectFragment
              }
              user {
                id
              }
            }
            sandboxOperationCollections {
              id
              name
            }
          }
        }
      }
      ${MultiOrgSelectFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const [duplicateSandboxCollectionToVariant] = useMutation<
    GraphQLTypes.DuplicateSandboxCollectionToVariant,
    GraphQLTypes.DuplicateSandboxCollectionToVariantVariables
  >(gql`
    mutation DuplicateSandboxCollectionToVariant(
      $collectionId: ID!
      $collectionName: String!
      $variantRef: ID
    ) {
      operationCollection(id: $collectionId) {
        duplicateCollection(
          isSandbox: false
          isShared: false
          name: $collectionName
          variantRef: $variantRef
        ) {
          ... on OperationCollection {
            id
            variants {
              id
              operationCollections {
                id
              }
            }
          }
        }
      }
    }
  `);

  const trackCustomEvent = useTrackCustomEvent();

  const sandboxCollections =
    data?.me?.__typename === 'User' ? data.me.sandboxOperationCollections : [];

  const orgs = useMemo(
    () =>
      data?.me?.__typename === 'User'
        ? data.me.memberships.map((membership) => membership.account)
        : undefined,
    [data],
  );
  const disabledOrgChecker = useCallback(
    (org: GraphQLTypes.MultiOrgSelectFragment) =>
      org.services.length === 0
        ? {
            isDisabled: true,
            disabledExplanation: 'There are no graphs in this organization.',
          }
        : {
            isDisabled: !org.roles?.canCreateService,
            disabledExplanation:
              "You don't have permission to create a graph in this organization.",
          },
    [],
  );

  const getNewGraphTitle = useCallback(() => {
    const existingGraphTitles = orgs?.flatMap((account) =>
      account.services.map(({ title }) => title),
    );
    return createUniqueName('My Graph', existingGraphTitles ?? []);
  }, [orgs]);

  const [defaultGraphTitle, setDefaultGraphTitle] =
    useState(getNewGraphTitle());

  const { createGraph, createGraphError } = useCreateGraph();
  const defaultInitialAccountId = useMemo(() => {
    const currentAccount = orgs?.find((org) => org.id === currentAccountId);
    if (currentAccount && !disabledOrgChecker(currentAccount).isDisabled)
      return currentAccountId;
    return orgs?.find((org) => !disabledOrgChecker(org).isDisabled)?.id;
  }, [currentAccountId, disabledOrgChecker, orgs]);

  const history = useHistory();
  const location = useLocation();

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormik<FormValues>({
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      graphTitle: defaultGraphTitle,
      graphId: generateGraphId(defaultGraphTitle),
      accountId: defaultInitialAccountId ?? currentAccountId,
      copySandboxCollectionsToVariant: true,
    },
    async onSubmit(formValues) {
      if (
        !(formValues.accountId && formValues.graphTitle && formValues.graphId)
      )
        return;
      const result = await createGraph({
        accountId: formValues.accountId,
        graphId: formValues.graphId,
        graphTitle: formValues.graphTitle,
        hiddenFromUninvitedNonAdminAccountMembers: false,
      });
      if (result) {
        // fire ga4 event
        trackCustomEvent({
          action: 'user_action',
          // Old event, this style of category is deprecated
          category: 'Added a graph' as EventCategory,
          label: 'User actions',
          legacyValueDimension: 'deployed',
        });
        await uploadSchema();

        if (
          sandboxCollections.length !== 0 &&
          formValues.copySandboxCollectionsToVariant
        ) {
          await Promise.all(
            sandboxCollections.map(async (collection) => {
              await duplicateSandboxCollectionToVariant({
                variables: {
                  collectionId: collection.id,
                  collectionName: collection.name,
                  variantRef: `${formValues.graphId}@current`,
                },
              });
            }),
          );
        }
        if (newGraphRef) {
          if (updateUrlError) {
            history.push(
              setOverlayVisibleLocation({
                location: referenceRouteConfig.location({ ...newGraphRef }),
                overlayName: Config.modals.connectionSettings,
              }),
            );
          } else if (!uploadSchemaError) {
            if (!isEmbeddableSandboxRoute()) {
              history.push(referenceRouteConfig.location({ ...newGraphRef }));
            } else {
              history.replace(setOverlayClosedLocation(location));
              openStudioLinkFromEmbedInNewTab(
                referenceRouteConfig.path({ ...newGraphRef }),
              );
            }
          }
        }
      }
    },
    validationSchema: baseGraphSetupValidationSchema,
  });

  useEffect(() => {
    if (!isSubmitting) {
      setDefaultGraphTitle(getNewGraphTitle());
    }
  }, [getNewGraphTitle, isSubmitting, orgs]);

  const selectedOrg = useMemo(
    () => orgs?.find((org) => org.id === values.accountId),
    [orgs, values.accountId],
  );
  const newGraphRef = useMemo(
    () =>
      values.graphId
        ? {
            graphId: values.graphId,
            graphVariant: Config.service.defaultVariant,
          }
        : undefined,
    [values.graphId],
  );

  const { uploadSchema, uploadSchemaError, updateUrlError } =
    useUploadSandboxSchema({
      selectedGraphRef: newGraphRef,
    });

  // store a unique string to add as a suffix so it doesn't regenerate when typing
  const [uniqueString, setUniqueString] = useState(generateUniqueString);
  const graphTitleError = touched.graphTitle && errors.graphTitle;
  const graphIdError = touched.graphId && errors.graphId;

  const [showGraphIdField, setShowGraphIdField] = useState(false);
  const graphIdAvailable = useQuery<
    GraphQLTypes.GraphIDAvailableQuery,
    GraphQLTypes.GraphIDAvailableQueryVariables
  >(GraphIDAvailableQuery, {
    variables: { accountId: values.accountId, graphId: values.graphId },
  });

  /**
   * The below 2 effects are shared between this component and `GraphSetupModal`
   * any changes should apply to both. If there is another instance where this is
   * needed, consider making a shared hook
   */

  // We want to keep the graph id in sync with graph title if the user hasn't editted yet.
  useEffect(() => {
    if (!touched.graphId) {
      setFieldValue('graphId', generateGraphId(values.graphTitle));
    }
  }, [setFieldValue, touched.graphId, values.graphTitle]);

  // If the user hasn't edited the id, we want to update the generate id so it's unique
  useEffect(() => {
    if (
      !graphIdAvailable.loading &&
      !graphIdAvailable.data?.account?.graphIDAvailable &&
      !touched.graphId
    ) {
      let nextId = generateGraphId(values.graphTitle, uniqueString);
      // This will catch if the unique string has already been used with this title and generate a new one
      if (nextId === values.graphId) {
        const nextUniqueString = generateUniqueString();
        setUniqueString(nextUniqueString);
        nextId = generateGraphId(values.graphTitle, nextUniqueString);
      }
      setFieldValue('graphId', nextId);
    }
  }, [
    setFieldValue,
    touched.graphId,
    values.graphTitle,
    uniqueString,
    values.graphId,
    graphIdAvailable.loading,
    graphIdAvailable.data?.account?.graphIDAvailable,
  ]);

  if (
    !loading &&
    orgs?.every(
      (org) =>
        disabledOrgChecker(org).isDisabled &&
        disabledOrgChecker(org).disabledExplanation.includes('permission'),
    )
  ) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Cannot create graph</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {`You do not have permission to create a graph ${
              orgs?.length && orgs.length > 1
                ? 'in any of your organizations.'
                : 'this organization'
            }`}
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={onClose} type="button">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <span className="flex items-center">
              Create a new graph{' '}
              {orgs?.length === 1 && orgs?.[0] && (
                <>
                  in {orgs[0].name}
                  <Avatar
                    className="mx-2"
                    name={orgs[0].name}
                    src={selectedOrg?.avatarUrl || undefined}
                    size="xs"
                    variant="org"
                  />
                </>
              )}
            </span>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="flex items-center">
              <Spinner className="m-auto" />
            </div>
          ) : (
            <>
              {isMinLengthArray(2, orgs) && (
                <div className="flex">
                  <div className="flex items-center rounded border border-primary">
                    <MultiOrgSelect
                      disabledItemChecker={disabledOrgChecker}
                      orgs={orgs}
                      selectedOrgId={values.accountId ?? null}
                      setSelectedOrgId={(orgId) =>
                        setFieldValue('accountId', orgId)
                      }
                      triggerContent={
                        <Button
                          variant="hidden"
                          size="sm"
                          className="-ml-1 min-w-0 items-center p-1"
                        >
                          {selectedOrg ? (
                            <>
                              <Avatar
                                name={selectedOrg.name}
                                src={selectedOrg.avatarUrl ?? undefined}
                                size="xs"
                                variant="org"
                              />
                              <span>{selectedOrg.name}</span>
                            </>
                          ) : (
                            <div className="size-5 rounded bg-silver-darker" />
                          )}
                        </Button>
                      }
                    />
                  </div>
                </div>
              )}
              <TextField
                autoFocus={true}
                disabled={isSubmitting}
                label={
                  <div className="flex items-center">
                    Graph title
                    <div className="mx-1 flex-1 truncate text-right font-mono text-sm font-normal text-secondary">
                      Graph ID: {values.graphId}
                    </div>
                    <Tooltip
                      label={
                        showGraphIdField ? 'Hide Graph ID' : 'Edit Graph ID'
                      }
                    >
                      <IconButton
                        aria-label={
                          showGraphIdField ? 'Hide Graph ID' : 'Edit Graph ID'
                        }
                        variant="hidden"
                        size="sm"
                        icon={<IconEdit />}
                        onClick={() => setShowGraphIdField((v) => !v)}
                        type="button"
                      />
                    </Tooltip>
                  </div>
                }
                name="graphTitle"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="e.g., my-graph"
                value={values.graphTitle}
                type="text"
                error={graphTitleError}
              />

              {showGraphIdField && (
                <TextFieldWithStatus
                  disabled={isSubmitting}
                  name="graphId"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldTouched('graphId', true);
                    handleChange(event);
                  }}
                  value={values.graphId}
                  type="text"
                  error={graphIdError}
                  status={
                    <Tooltip
                      label={
                        graphIdAvailable.loading
                          ? 'Loading graph ID availabiliy'
                          : graphIdAvailable.data?.account?.graphIDAvailable
                            ? 'Graph ID available'
                            : 'This graph ID is already taken '
                      }
                    >
                      {graphIdAvailable.loading ? (
                        <Spinner size="tiny" />
                      ) : graphIdAvailable.data?.account?.graphIDAvailable ? (
                        <IconCheck
                          className="rounded-full bg-green text-inverted"
                          style={{ padding: '0.325rem' }}
                        />
                      ) : (
                        <IconClose
                          className="cursor-pointer rounded-full bg-red text-inverted"
                          style={{ padding: '0.325rem' }}
                        />
                      )}
                    </Tooltip>
                  }
                />
              )}
              {sandboxCollections.length !== 0 && (
                <div className="flex items-center">
                  <Checkbox
                    className="mr-3 size-4"
                    isChecked={values.copySandboxCollectionsToVariant}
                    onChange={(e) =>
                      setFieldValue(
                        'copySandboxCollectionsToVariant',
                        e.target.checked,
                      )
                    }
                  />
                  <span>
                    Copy over Sandbox collections to{' '}
                    <span className="font-semibold">{values.graphTitle}</span>
                  </span>
                </div>
              )}
              {createGraphError && <ErrorMessage error={createGraphError} />}
              {uploadSchemaError && <ErrorMessage error={uploadSchemaError} />}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              variant="secondary"
              isDisabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={
                !(values.accountId && values.graphId && values.graphTitle) ||
                !!errors.graphId ||
                !!errors.graphTitle
              }
              data-analytics-category="Sandbox"
              data-analytics-label={`modal:Publish to new graph,graphId:${values.graphId}`}
              data-analytics-orgId={values.accountId}
              isLoading={isSubmitting}
              loadingText="Confirm"
              onClick={() => handleSubmit()}
            >
              Confirm
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
